import { RequestService } from '../request-service';
import type RpcModule from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/rpc';
import type HttpModule from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/http';

type RpcModuleType = typeof RpcModule;
type HttpModuleType = typeof HttpModule;

const CONNECTOR_CONFIG = 'CONNECTOR_CONFIG';
const FULL_PAGE_LIMIT = 100;

export interface ConnectorCatalogEntry {
  appId: string;
  compId: string;
  compName?: string;
  displayedPermissions: string[];
  description?: string;
  iconUrl?: string;
}

export interface ConnectorCatalogPage {
  entries: ConnectorCatalogEntry[];
  cursor?: string;
  hasNext: boolean;
}

interface ConnectorConfigData {
  description?: string;
  displayedPermissions?: string[];
  displayedPermissionItems?: { text?: string }[];
  iconUrl?: string;
}

interface RuntimeConnectorComponent {
  componentId?: string;
  name?: string;
  type?: string;
  data?: { connectorConfig?: ConnectorConfigData };
}

interface RuntimeAppData {
  appId?: string;
  components?: RuntimeConnectorComponent[];
}

interface GetAppsRuntimeDataByComponentTypesResponse {
  appsRuntimeData?: RuntimeAppData[];
  cursor?: string | null;
  hasNext?: boolean;
}

interface GetAppsRuntimeDataResponse {
  appsRuntimeData?: RuntimeAppData[];
}

// `displayedPermissionItems` is the translatable successor of `displayedPermissions`. Configs
// that predate it only fill the old list, so that one is still read when the new one is empty.
const readDisplayedPermissions = (config?: ConnectorConfigData): string[] => {
  const texts = (config?.displayedPermissionItems ?? []).flatMap((item) =>
    item.text ? [item.text] : [],
  );

  return texts.length > 0 ? texts : config?.displayedPermissions ?? [];
};

export class ConnectorCatalogService extends RequestService<
  RpcModuleType,
  HttpModuleType
> {
  protected async rpcModule(): Promise<RpcModuleType> {
    return import(
      '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/rpc'
    );
  }

  protected async httpModule(): Promise<HttpModuleType> {
    return import(
      '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/http'
    );
  }

  public async getCatalogPage({
    limit,
    cursor,
  }: {
    limit?: number;
    cursor?: string;
  }): Promise<ConnectorCatalogPage> {
    const response = await this.request<
      {
        componentTypes: string[];
        cursorPaging: { limit?: number; cursor?: string };
      },
      GetAppsRuntimeDataByComponentTypesResponse
    >({
      methodName: 'getAppsRuntimeDataByComponentTypes',
      payload: {
        componentTypes: [CONNECTOR_CONFIG],
        cursorPaging: {
          ...(limit ? { limit } : {}),
          ...(cursor ? { cursor } : {}),
        },
      },
    });

    return {
      entries: this.toEntries(response),
      cursor: response.cursor ?? undefined,
      hasNext: Boolean(response.hasNext),
    };
  }

  public async getCatalogByAppIds({
    appIds,
    componentIds,
    languageCode,
  }: {
    appIds: string[];
    componentIds?: string[];
    languageCode?: string;
  }): Promise<ConnectorCatalogEntry[]> {
    if (appIds.length === 0) {
      return [];
    }

    const response = await this.request<
      {
        appRuntimeKeys: { appId: string }[];
        componentTypes: string[];
        componentIds?: string[];
        languageCode?: string;
      },
      GetAppsRuntimeDataResponse
    >({
      methodName: 'getAppsRuntimeData',
      payload: {
        appRuntimeKeys: appIds.map((appId) => ({ appId })),
        componentTypes: [CONNECTOR_CONFIG],
        ...(componentIds && componentIds.length ? { componentIds } : {}),
        ...(languageCode ? { languageCode } : {}),
      },
    });

    const entries = this.toEntries(response);

    // The HTTP serializer drops `componentIds`, so every connector of the app comes back.
    return componentIds && componentIds.length
      ? entries.filter((entry) => componentIds.includes(entry.compId))
      : entries;
  }

  public async getCatalog(): Promise<ConnectorCatalogEntry[]> {
    const all: ConnectorCatalogEntry[] = [];
    let cursor: string | undefined;

    do {
      const page = await this.getCatalogPage({
        limit: FULL_PAGE_LIMIT,
        cursor,
      });
      all.push(...page.entries);
      cursor = page.hasNext ? page.cursor : undefined;
    } while (cursor);

    return all;
  }

  private toEntries(
    response: GetAppsRuntimeDataByComponentTypesResponse,
  ): ConnectorCatalogEntry[] {
    return (response.appsRuntimeData ?? []).flatMap((app) =>
      (app.components ?? [])
        .filter((component) => component.type === CONNECTOR_CONFIG)
        .map((component) => ({
          appId: app.appId ?? '',
          compId: component.componentId ?? '',
          compName: component.name ?? undefined,
          description:
            component.data?.connectorConfig?.description ?? undefined,
          displayedPermissions: readDisplayedPermissions(
            component.data?.connectorConfig,
          ),
          iconUrl: component.data?.connectorConfig?.iconUrl ?? undefined,
        })),
    );
  }
}
